import { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'

const API_BASE_URL = window.env.REACT_APP_DEMO_BACKEND_URL

/**
 * Custom hook for managing payment session timeout based on expires_at from API
 * @param {Object} options - Configuration options
 * @param {string} options.paymentSessionId - Payment session ID to fetch expiration data
 * @param {number} options.inactivityTimeout - Inactivity timeout in milliseconds (default: 15 minutes)
 * @param {number} options.maxWarnings - Maximum number of inactivity warnings (default: 4)
 * @param {Function} options.onTimeout - Callback when session expires
 * @param {Function} options.onInactivityWarning - Callback when inactivity warning occurs
 * @param {Function} options.onActivity - Callback when user activity is detected
 * @param {boolean} options.enabled - Whether the timeout is enabled (default: true)
 * @param {Array} options.events - Events to listen for user activity
 * @returns {Object} - Session timeout state and controls
 */
export const usePaymentSessionTimeoutFromAPI = ({
    paymentSessionId,
    inactivityTimeout = 15 * 60 * 1000, // 15 minutes default
    maxWarnings = 4,
    onTimeout = () => {},
    onInactivityWarning = () => {},
    onActivity = () => {},
    enabled = true,
    events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
} = {}) => {
    const [remainingTime, setRemainingTime] = useState(0)
    const [sessionDuration, setSessionDuration] = useState(0)
    const [isSessionExpired, setIsSessionExpired] = useState(false)
    const [isInactivityWarning, setIsInactivityWarning] = useState(false)
    const [warningCount, setWarningCount] = useState(0)
    const [lastActivityTime, setLastActivityTime] = useState(Date.now())
    const [expiresAt, setExpiresAt] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)

    const intervalRef = useRef(null)
    const lastActivityRef = useRef(Date.now())
    const currentWarningTimeoutRef = useRef(null)

    // Store callbacks in refs to avoid dependency issues
    const onTimeoutRef = useRef(onTimeout)
    const onInactivityWarningRef = useRef(onInactivityWarning)
    const onActivityRef = useRef(onActivity)

    // Update refs when callbacks change
    useEffect(() => {
        onTimeoutRef.current = onTimeout
        onInactivityWarningRef.current = onInactivityWarning
        onActivityRef.current = onActivity
    }, [onTimeout, onInactivityWarning, onActivity])

    // Fetch payment session data to get expires_at
    useEffect(() => {
        if (!enabled || !paymentSessionId) {
            setIsLoading(false)
            return
        }

        const fetchPaymentSession = async () => {
            try {
                setIsLoading(true)
                setError(null)

                // Fetch payment session data from the API
                const response = await axios.post(`${API_BASE_URL}/payment/session/${paymentSessionId}`)

                if (response.data && response.data.expiresAt) {
                    const expirationTime = new Date(response.data.expiresAt)
                    const now = new Date()

                    setExpiresAt(expirationTime)

                    // Calculate remaining time
                    const remaining = Math.max(0, expirationTime.getTime() - now.getTime())
                    setRemainingTime(remaining)
                    setSessionDuration(remaining)

                    // If already expired, trigger timeout immediately
                    if (remaining <= 0) {
                        setIsSessionExpired(true)
                        onTimeoutRef.current()
                    }
                } else {
                    throw new Error('No expiresAt field in payment session response')
                }
            } catch (err) {
                console.error('[Payment Session Timeout] Failed to fetch payment session:', err)
                setError(err.message)

                // Fallback to default 1 hour timeout if API fails
                const fallbackDuration = 60 * 60 * 1000 // 1 hour
                setSessionDuration(fallbackDuration)
                setRemainingTime(fallbackDuration)

                console.warn('[Payment Session Timeout] Using fallback 1-hour timeout due to API error')
            } finally {
                setIsLoading(false)
            }
        }

        fetchPaymentSession()
    }, [enabled, paymentSessionId])

    // Main countdown timer
    useEffect(() => {
        if (!enabled || isLoading || remainingTime <= 0) return

        // Clear any existing intervals
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }
        if (currentWarningTimeoutRef.current) {
            clearTimeout(currentWarningTimeoutRef.current)
        }

        // Reset activity tracking
        const now = Date.now()
        lastActivityRef.current = now
        setLastActivityTime(now)
        setIsInactivityWarning(false)
        setWarningCount(0)

        // Start main countdown
        intervalRef.current = setInterval(() => {
            const now = Date.now()
            const timeSinceActivity = now - lastActivityRef.current

            if (expiresAt) {
                // Calculate remaining time based on actual expiration time
                const remaining = Math.max(0, expiresAt.getTime() - now)
                setRemainingTime(remaining)

                // Check for session expiry
                if (remaining <= 0) {
                    clearInterval(intervalRef.current)
                    setIsSessionExpired(true)
                    setIsInactivityWarning(false)
                    onTimeoutRef.current()
                    return
                }
            }

            // Check for inactivity warning (only if we haven't reached max warnings)
            if (timeSinceActivity >= inactivityTimeout && !isInactivityWarning && warningCount < maxWarnings) {
                setIsInactivityWarning(true)
                setWarningCount(prev => prev + 1)
                onInactivityWarningRef.current()

                // Auto-dismiss warning after 2 minutes if no action taken
                currentWarningTimeoutRef.current = setTimeout(
                    () => {
                        setIsInactivityWarning(false)
                    },
                    2 * 60 * 1000,
                )
            }
        }, 1000)

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
            if (currentWarningTimeoutRef.current) {
                clearTimeout(currentWarningTimeoutRef.current)
            }
        }
    }, [enabled, isLoading, remainingTime, expiresAt, inactivityTimeout, maxWarnings, isInactivityWarning, warningCount])

    // Handle user activity
    const handleActivity = useCallback(() => {
        if (!enabled || isSessionExpired) return

        const now = Date.now()
        lastActivityRef.current = now
        setLastActivityTime(now)

        // Clear inactivity warning if active
        if (isInactivityWarning) {
            setIsInactivityWarning(false)
            if (currentWarningTimeoutRef.current) {
                clearTimeout(currentWarningTimeoutRef.current)
            }
        }

        onActivityRef.current()
    }, [enabled, isSessionExpired, isInactivityWarning])

    // Set up event listeners
    useEffect(() => {
        if (!enabled) return

        events.forEach(event => {
            document.addEventListener(event, handleActivity, true)
        })

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true)
            })
        }
    }, [enabled, events, handleActivity])

    // Extend session (reset inactivity timer)
    const extendSession = useCallback(() => {
        if (isSessionExpired) return

        const now = Date.now()
        lastActivityRef.current = now
        setLastActivityTime(now)
        setIsInactivityWarning(false)

        if (currentWarningTimeoutRef.current) {
            clearTimeout(currentWarningTimeoutRef.current)
        }
    }, [isSessionExpired])

    // Manually trigger timeout
    const triggerTimeout = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }
        if (currentWarningTimeoutRef.current) {
            clearTimeout(currentWarningTimeoutRef.current)
        }
        setIsSessionExpired(true)
        setIsInactivityWarning(false)
        onTimeoutRef.current()
    }, [])

    // Manually trigger inactivity warning
    const triggerInactivityWarning = useCallback(() => {
        if (warningCount >= maxWarnings || isSessionExpired) return

        setIsInactivityWarning(true)
        setWarningCount(prev => prev + 1)
        onInactivityWarningRef.current()

        // Auto-dismiss warning after 2 minutes
        currentWarningTimeoutRef.current = setTimeout(
            () => {
                setIsInactivityWarning(false)
            },
            2 * 60 * 1000,
        )
    }, [warningCount, maxWarnings, isSessionExpired])

    // Get time since last activity
    const getTimeSinceActivity = useCallback(() => {
        return Date.now() - lastActivityRef.current
    }, [])

    // Utility functions
    const getRemainingTimeFormatted = useCallback(() => {
        const hours = Math.floor(remainingTime / (60 * 60 * 1000))
        const minutes = Math.floor((remainingTime % (60 * 60 * 1000)) / (60 * 1000))
        const seconds = Math.floor((remainingTime % (60 * 1000)) / 1000)

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        }
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }, [remainingTime])

    const getInactivityTimeFormatted = useCallback(() => {
        const timeSinceActivity = getTimeSinceActivity()
        const minutes = Math.floor(timeSinceActivity / (60 * 1000))
        const seconds = Math.floor((timeSinceActivity % (60 * 1000)) / 1000)
        return `${minutes}:${seconds.toString().padStart(2, '0')}`
    }, [getTimeSinceActivity])

    return {
        // State
        remainingTime,
        sessionDuration,
        isSessionExpired,
        isInactivityWarning,
        warningCount,
        maxWarnings,
        lastActivityTime,
        expiresAt,
        isLoading,
        error,

        // Actions
        extendSession,
        triggerTimeout,
        triggerInactivityWarning,

        // Utilities
        getRemainingTimeFormatted,
        getInactivityTimeFormatted,
        getTimeSinceActivity,

        // Computed values
        canShowMoreWarnings: warningCount < maxWarnings,
        sessionProgress: sessionDuration > 0 ? ((sessionDuration - remainingTime) / sessionDuration) * 100 : 0,
    }
}

export default usePaymentSessionTimeoutFromAPI
